import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { Home, RefreshCw, Search, Send } from "react-feather";
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import Loader from "../../../components/Loader";
import { TableLoader } from "../../../components/LoaderC";
import Footer from "../../../components/Footer";
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";
import ReviewRequestDialog from "./components/ReviewRequestDialog";
import SendLinkDialog from "./components/SendLinkDialog";
import styles from "./vehiclerequests.module.css";
import {
  REQUEST_STATUS_TABS,
  REQUEST_TYPE_LABELS,
  STATUS_LABELS,
  apiErrorMessage,
  PASS_STATUS_PAID,
  formatDateTime,
  isGuestType,
  requesterName,
  vehicleText,
} from "./vehicleRequestUtils";

const SEARCH_DEBOUNCE_MS = 400;

const VehicleRequests = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState("PENDING");
  const [type, setType] = useState("");
  const [property, setProperty] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [propertyList, setPropertyList] = useState([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadCounter, setReloadCounter] = useState(0);
  const [reviewId, setReviewId] = useState(0);
  const [sendLinkOpen, setSendLinkOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    async function getProperties() {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const response = await axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-list", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPropertyList(response?.data?.data || []);
      } catch (error) {
        console.error("[VehicleRequests] Failed to load properties", error);
        toast.error(apiErrorMessage(error, "Unable to load properties for the filter."));
      }
    }
    getProperties();
  }, [getAccessTokenSilently]);

  const loadRequests = useCallback(async () => {
    setContentLoading(true);
    setLoadError("");
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests", {
        headers: { Authorization: `Bearer ${token}` },
        params: { status, type, property_id: property, search, page: page + 1, per_page: perPage },
      });
      setRows(response?.data?.data || []);
      setTotal(response?.data?.meta?.total || 0);
      setPendingCount(response?.data?.meta?.pending_count || 0);
    } catch (error) {
      console.error("[VehicleRequests] Failed to load vehicle requests", error);
      setRows([]);
      setTotal(0);
      setLoadError(apiErrorMessage(error, "Unable to load vehicle requests. Please try again."));
    } finally {
      setContentLoading(false);
    }
  }, [getAccessTokenSilently, status, type, property, search, page, perPage]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, reloadCounter]);

  const onReviewed = (message) => {
    setReviewId(0);
    toast.success(message);
    setReloadCounter((c) => c + 1);
  };

  return (<>
    <Helmet title="Vehicle Requests" />

    <div className={styles.pageTitleWrap}>
      <Typography display="inline" className={styles.pageTitle}>Vehicle Requests</Typography>
      <nav aria-label="breadcrumb">
        <ol className={styles.breadcrumb}>
          <li className={styles.breadcrumbItem}><span><Home /></span>Security</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Vehicle Requests</li>
        </ol>
      </nav>
    </div>

    <div className={styles.mainCard}>
      <div className={styles.topBar}>
        <Tabs value={status} onChange={(e, value) => { setStatus(value); setPage(0); }} variant="scrollable" scrollButtons="auto" className={styles.tabs}>
          {REQUEST_STATUS_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.value === "PENDING" && pendingCount > 0 ? `${tab.label} (${pendingCount})` : tab.label} />
          ))}
        </Tabs>
        <Button className={styles.sendLinkButton} startIcon={<Send size={16} />} onClick={() => setSendLinkOpen(true)}>Send update link</Button>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.searchBox}>
          <Search />
          <input type="text" placeholder="Search name, license, make, unit" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <FormControl size="small" className={styles.filterSelect}>
          <InputLabel id="vr-type-label">Type</InputLabel>
          <Select labelId="vr-type-label" label="Type" value={type} onChange={(e) => { setType(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="TENANT">Tenant</MenuItem>
            <MenuItem value="GUEST">Guest</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" className={styles.filterSelect}>
          <InputLabel id="vr-property-label">Property</InputLabel>
          <Select labelId="vr-property-label" label="Property" value={property} onChange={(e) => { setProperty(e.target.value); setPage(0); }}>
            <MenuItem value="all">Any</MenuItem>
            {propertyList.map((item) => (<MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>))}
          </Select>
        </FormControl>
      </div>

      {loadError !== "" && (<div className={styles.stateBox}>
        <p>{loadError}</p>
        <Button startIcon={<RefreshCw size={16} />} onClick={() => setReloadCounter((c) => c + 1)}>Try again</Button>
      </div>)}

      {loadError === "" && (<TableContainer className={styles.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Submitted</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Property / Unit</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>License</TableCell>
              <TableCell>Pass</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contentLoading && (<TableRow><TableCell colSpan={9}><div className="Loader TableLoader"><TableLoader /></div></TableCell></TableRow>)}
            {!contentLoading && rows.length === 0 && (<TableRow><TableCell colSpan={9} className={styles.emptyCell}>
              {status === "PENDING" ? "No requests are waiting for review." : "No vehicle requests found."}
            </TableCell></TableRow>)}
            {!contentLoading && rows.map((row) => (<TableRow key={row.id} hover>
              <TableCell>{formatDateTime(row.submitted_at)}</TableCell>
              <TableCell><Chip size="small" className={isGuestType(row.type) ? styles.chipGuest : styles.chipTenant} label={REQUEST_TYPE_LABELS[row.type] || row.type} /></TableCell>
              <TableCell>{requesterName(row)}</TableCell>
              <TableCell>{row.property_name || "-"}{row.apt_no ? ` · ${row.apt_no}` : ""}</TableCell>
              <TableCell>{vehicleText(row)}{row.photo_count > 0 ? ` (${row.photo_count} photo${row.photo_count > 1 ? "s" : ""})` : ""}</TableCell>
              <TableCell>{row.license || "-"}</TableCell>
              <TableCell>{row.type !== "GUEST" ? "-" : Number(row.pass_status) === PASS_STATUS_PAID ? `${row.pass_type} · $${Number(row.pass_amount || 0).toFixed(2)} paid` : row.pass_type ? "Awaiting payment" : "After approval"}</TableCell>
              <TableCell><span className={`${styles.statusBadge} ${styles["status" + row.status] || ""}`}>{STATUS_LABELS[row.status] || row.status}</span></TableCell>
              <TableCell align="right">
                <Button size="small" className={styles.reviewButton} onClick={() => setReviewId(row.id)}>{row.status === "PENDING" ? "Review" : "View"}</Button>
              </TableCell>
            </TableRow>))}
          </TableBody>
        </Table>
      </TableContainer>)}

      {loadError === "" && total > 0 && (<TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, value) => setPage(value)}
        rowsPerPage={perPage}
        onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />)}
    </div>

    {reviewId > 0 && (<ReviewRequestDialog requestId={reviewId} onClose={() => setReviewId(0)} onReviewed={onReviewed} />)}
    {sendLinkOpen && (<SendLinkDialog onClose={() => setSendLinkOpen(false)} onSent={(message) => { setSendLinkOpen(false); toast.success(message); }} />)}

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(VehicleRequests, {
  onRedirecting: () => <Loader />,
});
